import { buttonStyle } from 'components/buttons/buttonStyle';
import NumberInput from 'components/forms/inputs/rhf-inputs/NumberInput.tsx';
import { DataTable } from 'components/tables/data-table';
import { Button } from 'components/ui/button';
import { useId, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { useUpdateDraftRetentionDaysMutation } from 'store/api/tenants.ts';
import { DraftRetentionDaysUpdateDto, TenantConfigurationDto } from 'types/tenants.ts';
import { t } from 'utils/getTranslations.ts';
import { useHasPermissions } from 'utils/permissionUtil';

interface Props {
    tenantConfiguration: TenantConfigurationDto;
}

/** How long a draft nobody saves is kept. Hidden with the drafts flag off, though drafts stored before still expire. */
const DraftRetentionDaysControl = ({ tenantConfiguration }: Props): JSX.Element => {
    const [updateDraftRetentionDays] = useUpdateDraftRetentionDaysMutation();
    const hasUpdatePermission = useHasPermissions(['UPDATE.BACK_OFFICE_SETTINGS']);
    const hintId = useId();

    const { handleSubmit, control } = useForm<DraftRetentionDaysUpdateDto>({
        defaultValues: {
            draftRetentionDays: tenantConfiguration.draftRetentionDays,
        },
    });

    const columns = useMemo<DataTableColumnDef<TenantConfigurationDto>[]>(
        () => [
            {
                header: t('configuration.draft-retention'),
                id: 'draft-retention',
                enableSorting: false,
                cell: () => (
                    <div className="new:grid-cols-[16.25rem_auto_1fr] grid grid-cols-[19.75rem_auto_1fr] items-center gap-x-10 gap-y-2">
                        <p className="text-body2">
                            <FormattedMessage id="configuration.draft-retention-days" />
                        </p>
                        <NumberInput
                            control={control}
                            name="draftRetentionDays"
                            inputProps={{
                                min: 1,
                                max: 3650,
                                'aria-label': t('configuration.draft-retention-days'),
                                'aria-describedby': hintId,
                            }}
                            suffixComponent={<FormattedMessage id="configuration.days" />}
                            required
                            disabled={!hasUpdatePermission}
                            wrapperClassName="w-24"
                        />
                        <Button
                            {...buttonStyle({ variant: 'contained', color: 'secondary' }, 'h-8 justify-self-start')}
                            onClick={() => {
                                void handleSubmit((data) => updateDraftRetentionDays(data))();
                            }}
                            disabled={!hasUpdatePermission}
                        >
                            <FormattedMessage id="save" />
                        </Button>
                        <p
                            id={hintId}
                            className="text-caption text-muted-foreground col-span-2 col-start-2 text-pretty"
                        >
                            <FormattedMessage id="configuration.draft-retention-days-hint" />
                        </p>
                    </div>
                ),
            },
        ],
        [control, handleSubmit, hasUpdatePermission, hintId, updateDraftRetentionDays]
    );

    const rows = useMemo(() => [tenantConfiguration], [tenantConfiguration]);

    return (
        <>
            <div className="flex justify-between">
                <h3 className="text-h3 text-balance">
                    <FormattedMessage id="configuration.draft-retention-days-control" />
                </h3>
            </div>
            <DataTable<TenantConfigurationDto>
                columns={columns}
                data={rows}
                columnConfigId="configuration-draft-retention-days"
            />
        </>
    );
};

export default DraftRetentionDaysControl;
